import { Component, type SyntheticEvent } from "react";
import { observer } from "mobx-react";
import { Stack, TextField, DefaultButton } from "@fluentui/react";
import { action, computed, makeObservable, observable } from "mobx";
import { type PedestrianDeviceCreate, type PedestrianDevice } from "../../api";
import { doubleBindNullableStringEmptyAsNull, doubleBindString } from "../../utils/double-bind";
import { type FormCreateProps } from "../../utils/form-create-props";
import { createUuid } from "../../utils/uuid";
import { PrimaryButtonValidation } from "../atoms/primary-button-validation";
import { ElofleetDialogFooter } from "../atoms/elofleet-dialog-footer";
import sizes from "../sizes.scss";

export interface FormCreatePedestrianDeviceProps extends FormCreateProps<PedestrianDevice> {}

import { store } from "../../store";

@observer
export class FormCreatePedestrianDevice extends Component<FormCreatePedestrianDeviceProps> {
    @observable public accessor label: string | undefined;
    @observable public accessor hardwareSerialNumber = "";

    private validationId = createUuid();

    constructor(props: FormCreatePedestrianDeviceProps) {
        super(props);
        makeObservable(this);
    }

    private triggerValidation(): void {
        store.repositoryPedestrianDevices.validation.create.updateModel(
            this.validationId,
            this.pedestrianDeviceCreate,
        );
    }

    componentDidMount(): void {
        store.repositoryPedestrianDevices.validation.create.initializeModel(
            this.validationId,
            this.pedestrianDeviceCreate,
        );
    }

    @computed private get pedestrianDeviceCreate(): PedestrianDeviceCreate {
        const { label, hardwareSerialNumber } = this;
        return { label, hardwareSerialNumber };
    }

    @action.bound
    private async createPedestrianDevice(evt: SyntheticEvent<HTMLFormElement>): Promise<void> {
        evt.preventDefault();
        const pedestrianDevice = await store.repositoryPedestrianDevices.create(
            this.pedestrianDeviceCreate,
        );
        this.reset();
        if (this.props.onCreate) {
            this.props.onCreate(pedestrianDevice);
        }
    }

    @action.bound private reset(): void {
        this.label = "";
        this.triggerValidation();
    }

    public render(): JSX.Element {
        const primaryButton = (
            <PrimaryButtonValidation
                text={store.i18n.t("formCreatePedestrianDevice.submit.text")}
                validation={store.repositoryPedestrianDevices.validation.create}
                validationId={this.validationId}
            />
        );
        return (
            <form onSubmit={this.createPedestrianDevice}>
                <Stack tokens={{ padding: `0px ${sizes.formPaddingHorizontal}` }}>
                    <TextField
                        label={store.i18n.t("formCreatePedestrianDevice.label.label")}
                        {...doubleBindNullableStringEmptyAsNull<FormCreatePedestrianDevice>(
                            this,
                            "label",
                            () => this.triggerValidation(),
                        )}
                        errorMessage={store.i18n.formatFieldValidationState(
                            store.repositoryPedestrianDevices.validation.create.getFieldValidationState(
                                this.validationId,
                                "label",
                            ),
                        )}
                    />
                    <TextField
                        label={store.i18n.t(
                            "formCreatePedestrianDevice.hardwareSerialNumber.label",
                        )}
                        {...doubleBindString<FormCreatePedestrianDevice>(
                            this,
                            "hardwareSerialNumber",
                            () => this.triggerValidation(),
                        )}
                        required
                        errorMessage={store.i18n.formatFieldValidationState(
                            store.repositoryPedestrianDevices.validation.create.getFieldValidationState(
                                this.validationId,
                                "hardwareSerialNumber",
                            ),
                        )}
                    />
                </Stack>
                {this.props.asDialogContent ? (
                    <ElofleetDialogFooter>
                        <DefaultButton
                            text={store.i18n.t("formCreatePedestrianDevice.cancel.text")}
                            onClick={this.props.onDialogCancel}
                        />
                        {primaryButton}
                    </ElofleetDialogFooter>
                ) : (
                    <Stack horizontal horizontalAlign="end">
                        {primaryButton}
                    </Stack>
                )}
            </form>
        );
    }
}
